<template>
  <div>
    <v-text-field
      required
      outlined
      dense
      class="body2"
      data-testid="createOrganization-input-name"
      :rules="[name => !!name || $t('validation.form.this_field_is_required')]"
      :value="organization.name"
      :error-messages="errors.organizationName"
      :maxlength="80"
      :label="`${$t('signup.company_name')}*`"
      @input="$emit('onChange', $event, 'organizationName')"
    />
    <v-text-field
      append-icon="mdi-lock"
      class="body2"
      data-private
      data-testid="createOrganization-input-email"
      dense
      outlined
      required
      :error-messages="errors.email"
      :hint="$t('signup.email_hint')"
      :label="`${$t('auth.register.email')}*`"
      :rules="emailRules"
      :value="account.email"
      @input="$emit('onChange', $event, 'email')"
    />
    <v-text-field
      required
      outlined
      dense
      data-private
      class="body2"
      data-testid="createOrganization-input-firstName"
      v-model="account.displayname"
      :label="`${$t('auth.register.first_name')}*`"
      :rules="[name => !!name || $t('validation.form.this_field_is_required')]"
    />
    <v-text-field
      required
      outlined
      dense
      data-private
      class="body2"
      data-testid="createOrganization-input-lastName"
      v-model="account.familyname"
      :label="`${$t('auth.register.last_name')}*`"
      :rules="[name => !!name || $t('validation.form.this_field_is_required')]"
    />
    <v-text-field
      required
      outlined
      dense
      data-private
      data-testid="createOrganization-input-position"
      v-model="account.position"
      :maxlength="$constants.profile.positionMaxLength"
      :label="`${$t('profile_settings.job_position')}*`"
      :rules="[name => !!name || $t('validation.form.this_field_is_required')]"
    />
    <AddPassword
      testId="createOrganization-input-password"
      :password="form.password"
      :confirmPassword="form.confirmPassword"
      @change="handlePasswordChange"
    />
    <div
      v-if="showTermsOfService"
      class="d-flex align-center toc-checkbox-container body-2 mt-2"
    >
      <v-checkbox
        hide-details="auto"
        data-testid="createOrganization-checkbox-tServices"
        v-model="account.tocsHasAccepted"
      />
      <label class="mb-0"
        >{{ $t('auth.register.i_agree_to_the_tocs') }}
        <a target="_blank" :href="$t('auth.access.terms_of_service_url')">{{
          $t('auth.register.terms_of_service')
        }}</a>
        {{ $t('common.and') }}
        <a target="_blank" :href="$t('auth.access.privacy_policy_url')">{{
          $t('auth.access.privacy_policy')
        }}</a>
      </label>
    </div>
    <div v-if="showGDPRCheckbox" class="d-flex align-center body-2 mt-2">
      <v-checkbox
        class="mt-0"
        hide-details="auto"
        data-testid="createOrganization-checkbox-gdpr"
        v-model="account.gdprApproval"
      />
      <label class="mt-2"
        >{{ $t('auth.register.i_agree_to_receive_marketing_communications') }}
      </label>
    </div>
    <Button
      block
      data-testid="createOrganization-button-next"
      class="mt-6"
      color="primary"
      :disabled="requiresTOCs || !isFormValid"
      :text="buttonText"
      @click="onSubmit"
    />
    <div class="d-flex justify-center mt-6">
      <p
        class="caption"
        v-html="$t('authManagePeople.already_account', { href: '/access' })"
      />
    </div>
  </div>
</template>

<script>
import validationHelper from '@/lib/validationHelper.js'

import Button from '@/components/UI/Button'
import AddPassword from '@/components/AddPassword.vue'

export default {
  name: 'SignUpForm',

  props: {
    account: {
      type: Object,
      required: false,
    },
    organization: {
      type: Object,
      required: false,
    },
    buttonText: {
      type: String,
      required: true,
    },
    showTermsOfService: {
      type: Boolean,
      required: false,
      default: false,
    },
    showGDPRCheckbox: {
      type: Boolean,
      required: false,
      default: false,
    },
    errors: {
      type: Object,
      required: false,
    },
    isFormValid: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      form: {
        password: '',
        confirmPassword: '',
      },

      emailRules: [
        value =>
          !!value?.trim?.() ||
          this.$t('validation.form.this_field_is_required'),
        email =>
          !this.$constants.organization.domainsBlacklist.includes(
            this.getDomain(email)
          ) || this.$t('organization.domain_is_insecure_to_add'),

        email =>
          validationHelper.isValidEmailAddress(email) ||
          this.$t('validation.user.please_enter_a_valid_email_address'),
      ],
    }
  },

  components: {
    AddPassword,
    Button,
  },

  computed: {
    requiresTOCs() {
      return this.showTermsOfService && !this.account.tocsHasAccepted
    },
  },

  methods: {
    handlePasswordChange(payload) {
      this.form = { ...this.form, ...payload }
      if (payload.password) {
        this.$emit('onChangePassword', this.form.password)
      }
    },

    getDomain(email) {
      return email?.replace?.(/.*@/, '') ?? ''
    },

    onSubmit() {
      this.$emit('onSubmit', this.account)
    },
  },
}
</script>

<style lang="scss" scoped>
.toc-checkbox-container {
  .v-input {
    margin-top: 0;
    padding-top: 0;
  }
}
</style>
